import React, { useState } from "react";
import axios from "axios";
import { Formik, Field, Form, ErrorMessage, FormikHelpers } from "formik";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import ConfirmationModal from "../../shared/ConfirmationModal";
import NewconfirmationModal from "../../shared/NewconfirmationModal";
import AllFailureModal from "../../shared/AllFailureModal";

interface DegreeFormValues {
  degree_name: string;
}

const DegreeAdd: React.FC = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<DegreeFormValues | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [failureMessage, setFailureMessage] = useState("");

  const headers = {
    "x-access-token": localStorage.getItem("jwttoken") || "",
  };

  const validateForm = (values: DegreeFormValues) => {
    const errors: Partial<DegreeFormValues> = {};
    if (!values.degree_name.trim()) {
      errors.degree_name = "Degree name is required";
    }
    return errors;
  };

  const callApiDegreeAdd = async (values: DegreeFormValues) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}degree/add_degree`;
      const response = await axios.post(url, values, { headers });
      if (response.status === 201) {
        setSuccessMessage("Degree added successfully.");
        setShowSuccessModal(true);
        setTimeout(() => {
          navigate("/degreelist");
        }, 3000);
      }
    } catch (error: any) {
      console.error("Error adding degree:", error);
      if (error.response) {
        if (error.response.status === 409) {
          setFailureMessage("Degree already exists. Please try another name.");
        } else {
          setFailureMessage(error.response.data.error || "Failed to add degree. Please try again.");
        }
      } else {
        setFailureMessage("Failed to add degree. Please check your connection.");
      }
      setShowFailureModal(true);
    }
  };

  const handleSubmit = (values: DegreeFormValues, actions: FormikHelpers<DegreeFormValues>) => {
    setFormValues(values);
    setModalAction("Confirm");
    setShowModal(true);
  };

  const handleConfirm = async () => {
    if (modalAction === "Confirm" && formValues) {
      await callApiDegreeAdd(formValues);
    }
    setShowModal(false);
  };

  const handleCancel = () => {
    setShowModal(false);
  };

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={8}>
          <Card className="shadow">
            <Card.Header className="bg-info text-white text-center">
              <h4>Add New Degree</h4>
            </Card.Header>
            <Card.Body>
              <Formik
                initialValues={{ degree_name: "" }}
                validate={validateForm}
                validateOnMount={true}
                onSubmit={handleSubmit}
              >
                {({ errors, touched }) => (
                  <Form>
                    <Row className="mb-3">
                      <Col md={12}>
                        <label htmlFor="degree_name" className="form-label">
                          Degree Name:
                        </label>
                        <Field
                          name="degree_name"
                          type="text"
                          className={`form-control ${
                            errors.degree_name && touched.degree_name ? "is-invalid" : ""
                          }`}
                          placeholder="Enter Degree Name"
                        />
                        <ErrorMessage name="degree_name" component="div" className="text-danger" />
                      </Col>
                    </Row>
                    <Row className="mt-4">
                      <Col className="text-center">
                        <Button type="submit" variant="success">
                          Add Degree
                        </Button>
                        <Button variant="danger" className="mx-2" onClick={() => navigate("/degreelist")}>
                          Cancel
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <ConfirmationModal
        show={showModal}
        onHide={handleCancel}
        modalMessage="Are you sure you want to add this degree?"
        operationType={modalAction}
        confirmation={handleConfirm}
      />

      <NewconfirmationModal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        modalMessage={successMessage}
        operationType="OK"
        timeoutDuration={3000}
      />

      <AllFailureModal
        show={showFailureModal}
        onHide={() => setShowFailureModal(false)}
        modalMessage={failureMessage}
        operationType="OK"
        timeoutDuration={3000}
      />
    </Container>
  );
};

export default DegreeAdd;
